import React, { Component } from 'react';
import { authLocalStore, credentials } from '@shootsta/client-auth';
import { replaceBucketKeysWithUrls } from '@shootsta/client-aws-s3';
import {
  CreditsInfo,
  Icon,
  Image,
  isPublicUrl,
  withFlagsConsumer
} from '@shootsta/common-react';
import type { User, RouterHistory as History } from '@core/shared';
import NotificationsSummary from './NotificationsSummary';
import UserWrapper from './UserWrapper';
import SwitchOrganisationModal from './SwitchOrganisationModal';
import { clearSSOLocalStore } from '../../../../utils';
import { handleSignOut } from '../../../utils';
import SHOOTSTA_LOGO_ICON from '../../../../assets/images/shootsta-logo-icon.png';
import { PATHS_TO_HIDE_NAVBAR } from '../../../constants';
import {
  openHubSpotConversationsWidget,
  TopupCreditsModal
} from '@core/shared';

import '../styles/_global-header.scss';

type GlobalHeaderProps = {
  showSwitchOrganisationModal: any;
  appSwitcherVisible: boolean;
  onCloseSwitchOrganisationModal: any;
  apolloClient: any;
  selectedOrganisation: any;
  notifications: any;
  history: History;
  adminMode: boolean;
  toggleAdminMode: any;
  ghostStop: any;
  getFeedbackLink: any;
  loginViaIdentity: boolean;
  width: number;
  flags: any;
};

type GlobalHeaderState = {
  userId: string;
  fullName: string;
  imageUrl: string;
  email: string;
  feedbackLinks?: any;
  isGhosting: boolean;
  original: any;
  origURL: string;
  orgImageUrl: string | null;
  showTopUpCreditModal: boolean;
};

const MAX_FULL_HEADER_WIDTH = 500;

class GlobalHeader extends Component<GlobalHeaderProps, GlobalHeaderState> {
  constructor(props: GlobalHeaderProps) {
    super(props);

    const { userId, firstName, lastName, imageUrl, email } =
      credentials.get().user;
    const { original = null, origURL = '' } = credentials.get();
    credentials.listenToChange(this.handleCredentialsChange);

    const fullName = `${firstName} ${lastName}`;

    this.state = {
      userId,
      fullName,
      imageUrl,
      orgImageUrl: null,
      email,
      feedbackLinks: null,
      isGhosting: Boolean(original),
      original,
      origURL,
      showTopUpCreditModal: false
    };
  }

  componentDidMount() {
    const { flags } = this.props;
    this.getPreSignedImageUrl();
    this.getPreSignedOrgImageUrl();
    window.addEventListener(
      'feedbackLinksUpdated',
      this.handleFeedbackLinksUpdated
    );
    if (flags?.enableFeedbackLinks) {
      this.fetchFeedbackLinks();
    }
  }

  componentDidUpdate(prevProps: GlobalHeaderProps) {
    const wasEnabled = !!prevProps.flags?.enableFeedbackLinks;
    const isEnabled = !!this.props.flags?.enableFeedbackLinks;
    if (!wasEnabled && isEnabled && !this.state.feedbackLinks) {
      this.fetchFeedbackLinks();
    }
  }

  componentWillUnmount() {
    window.removeEventListener(
      'feedbackLinksUpdated',
      this.handleFeedbackLinksUpdated
    );
  }

  showTopupCreditModal = () => {
    this.setState({
      showTopUpCreditModal: true
    });
  };

  onCloseTopupCreditModal = () => {
    this.setState({
      showTopUpCreditModal: false
    });
  };

  fetchFeedbackLinks = async () => {
    const { getFeedbackLink } = this.props;
    try {
      const payload = ['topbarFeedbackIconLink'];
      const { data } = await getFeedbackLink(payload);
      this.setState({ feedbackLinks: data?.getFeedbackLinks });
    } catch (error: any) {
      console.error(error);
    }
  };

  handleFeedbackLinksUpdated = (event: Event) => {
    const { detail } =
      event as CustomEvent<{ topbarFeedbackIconLink?: string } | null>;
    if (detail && typeof detail.topbarFeedbackIconLink !== 'undefined') {
      const nextTopbarLink = detail.topbarFeedbackIconLink;
      this.setState((prev) => ({
        feedbackLinks: {
          ...(prev.feedbackLinks || {}),
          topbarFeedbackIconLink: nextTopbarLink
        }
      }));
      return;
    }
    this.fetchFeedbackLinks();
  };

  getPreSignedImageUrl = async () => {
    const {
      selectedOrganisation: { organisationId },
      apolloClient
    } = this.props;
    const { imageUrl } = this.state;

    try {
      const { imageUrl: preSignedImageUrl } = await replaceBucketKeysWithUrls({
        apiClient: apolloClient,
        data: { imageUrl },
        substitutedKeys: ['imageUrl'],
        organisationId
      });

      this.setState({ imageUrl: preSignedImageUrl });
    } catch {
      // Silently handle error
    }
  };

  getPreSignedOrgImageUrl = async () => {
    const {
      selectedOrganisation: { organisationId, imageUrl },
      apolloClient
    } = this.props;

    try {
      const { imageUrl: preSignedOrgImageUrl } =
        await replaceBucketKeysWithUrls({
          apiClient: apolloClient,
          data: { imageUrl },
          substitutedKeys: ['imageUrl'],
          organisationId
        });

      this.setState({ orgImageUrl: preSignedOrgImageUrl });
    } catch {
      // Silently handle error
    }
  };

  handleCredentialsChange = (userCredentials?: {
    user: User | null | undefined;
  }) => {
    if (!userCredentials || !userCredentials.user) {
      return;
    }
    const { user } = userCredentials;
    this.setState(
      {
        ...user,
        fullName: `${user.firstName} ${user.lastName}`
      },
      this.getPreSignedImageUrl
    );
  };

  handleSignOut = () => {
    const { isGhosting } = this.state;
    const { history, loginViaIdentity } = this.props;

    return handleSignOut({
      isGhosting,
      history,
      loginViaIdentity
    });
  };

  handleStopGhosting = async () => {
    const { ghostStop, history, loginViaIdentity } = this.props;
    const {
      original: { token, user, original = null },
      origURL
    } = this.state;

    clearSSOLocalStore();

    if (!loginViaIdentity) {
      return void history.push(
        `/?ghost=${btoa(JSON.stringify({ clearGhost: true, token, user, original, targetURL: origURL }))}`
      );
    }

    await ghostStop();

    const data = {
      auth: true,
      ghost: false,
      token,
      user,
      original,
      origURL
    } as const;

    authLocalStore.save(data);

    window.location.replace(origURL);
  };

  renderContent() {
    const { width, flags } = this.props;
    const { feedbackLinks } = this.state;

    if (width < MAX_FULL_HEADER_WIDTH) {
      return null;
    }

    if (flags.enableFeedbackLinks && feedbackLinks?.topbarFeedbackIconLink) {
      return (
        <div className="global-header-container__changes-container">
          <Icon
            tooltipId="feedback"
            name="feedback"
            tooltipLabel="Feedback"
            size={20}
            tooltipPlace="bottom"
            onClick={() =>
              window.open(feedbackLinks.topbarFeedbackIconLink, '_blank')
            }
          />
        </div>
      );
    }

    return null;
  }

  render() {
    const {
      userId,
      fullName,
      imageUrl,
      email,
      feedbackLinks,
      isGhosting,
      orgImageUrl,
      showTopUpCreditModal
    } = this.state;
    const {
      showSwitchOrganisationModal,
      selectedOrganisation,
      appSwitcherVisible,
      onCloseSwitchOrganisationModal,
      notifications,
      adminMode,
      toggleAdminMode,
      width,
      flags
    } = this.props;

    const {
      workflowV5 = false,
      enabledPaymentGateway,
      disablePlatformNotificationBellIcon
    } = flags || {};

    const isV5ProjectListingPage =
      (workflowV5 && location.pathname.includes('/pro')) ||
      (workflowV5 && location.pathname.includes('/review'));
    const isNotProBuilderPage = !location.pathname.includes('/pro/builder/new');

    // Don't show CreditsInfo for public URLs - it triggers getOrganisation API which fails for guests
    const showCreditInfo =
      !isPublicUrl() &&
      (PATHS_TO_HIDE_NAVBAR.some((path) =>
        location.pathname.startsWith(path)
      ) ||
        isV5ProjectListingPage);

    const showOrgLogo =
      PATHS_TO_HIDE_NAVBAR.some((path) => location.pathname.startsWith(path)) &&
      isNotProBuilderPage;

    return (
      <header>
        <div className="column global-header-container">
          {showOrgLogo && (
            <img
              className="workspace-logo"
              width={35}
              height={35}
              onClick={() => this.props.history.push('/home')}
              src={orgImageUrl || SHOOTSTA_LOGO_ICON}
            />
          )}
          {showCreditInfo && (
            <CreditsInfo
              openHubSpotConversationsWidget={openHubSpotConversationsWidget}
              onClickTopUpNow={
                enabledPaymentGateway && this.showTopupCreditModal
              }
            />
          )}
          {this.renderContent()}
          {!disablePlatformNotificationBellIcon && (
            <NotificationsSummary {...notifications} />
          )}
          <UserWrapper
            userId={userId}
            fullName={fullName}
            imageUrl={imageUrl}
            isGhosting={isGhosting}
            email={email}
            handleSignOut={this.handleSignOut}
            handleStopGhosting={this.handleStopGhosting}
            showSwitchOrganisationModal={showSwitchOrganisationModal}
            selectedOrganisation={selectedOrganisation}
            adminMode={adminMode}
            toggleAdminMode={toggleAdminMode}
          />
          <SwitchOrganisationModal
            visible={appSwitcherVisible}
            onClose={onCloseSwitchOrganisationModal}
          />
          {showTopUpCreditModal && (
            <TopupCreditsModal
              visible={showTopUpCreditModal}
              onClose={this.onCloseTopupCreditModal}
            />
          )}
        </div>
      </header>
    );
  }
}

export default withFlagsConsumer(GlobalHeader);
